import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Button, Modal, notification, Space, Spin, Tabs } from 'antd'
import { useSelector } from 'react-redux'

import ExamineeApplyFormTable from './ExamineeApplyFormTable'
import ExamineeInfo from './ExamineeInfo'
import ExamineePublicResult from './ExamineePublicResult'
import { getExamApplyTemplate, getExamineeApplyInfo, updateExamineeCheckStatus } from '../../common/request'
import ResponseCode from '../../common/response-code'
import RefuseCheckModal from './RefuseCheckModal'
import ExamineeAdmissionTicket from './ExamineeAdmissionTicket'
import ExamineeInterviewAdmission from './ExamineeInterviewAdmission'
import ChsiReportVerificationDetailModal from './ChsiReportVerificationDetailModal'
import eventBus from '../../plugins/events'
import DraggableModal from '../DraggableModal'
import PaymentInformation from './PaymentInformation'
import { canRoleCheckExamineeByReviewStage } from '../../common/common'

const CHSI_POLL_INTERVAL_MS = 10000
const CHSI_POLL_MAX_TIMES = 3

const ExamineeInfoModal = forwardRef((props, ref) => {
  const refuseCheckModalRef = useRef()
  const chsiDetailModalRef = useRef()
  const chsiPollTimerRef = useRef(null)
  const chsiPollCountRef = useRef(0)
  const fetchRequestIdRef = useRef(0)
  const [modalVisible, setModalVisible] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [applyInfo, setApplyInfo] = useState({})
  const [templateField, setTemplateField] = useState()
  const [examineeTabsItems, setExamineeTabsItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeKey, setActiveKey] = useState('examineeInfo')
  const userInfo = useSelector(state => state.userInfo.userInfo)
  const selectedExam = useSelector(state => state.examInfo.selectedExam)

  useImperativeHandle(ref, () => {
    return {
      showModal
    }
  })

  const clearChsiPollTimer = useCallback(() => {
    if (chsiPollTimerRef.current) {
      clearTimeout(chsiPollTimerRef.current)
      chsiPollTimerRef.current = null
    }
  }, [])

  const resetChsiPoll = useCallback(() => {
    clearChsiPollTimer()
    chsiPollCountRef.current = 0
  }, [clearChsiPollTimer])

  const resetExamineeData = useCallback(() => {
    fetchRequestIdRef.current += 1
    setApplyInfo({})
    setTemplateField(undefined)
    setExamineeTabsItems([])
    setLoading(false)
    setModalTitle('')
    setActiveKey('examineeInfo')
    resetChsiPoll()
  }, [resetChsiPoll])

  const getApplyInfoParams = useCallback(() => {
    return {
      applyInfoId: props.examineeItem.applyInfoId || props.examineeItem.id,
      examId: props.examineeItem.examId,
      positionNumber: props.examineeItem.positionNumber,
      userId: props.examineeItem.userId
    }
  }, [props.examineeItem])

  const getExamineeApplyInfoAction = useCallback((options = {}) => {
    const { silent = false } = options
    const requestId = ++fetchRequestIdRef.current

    if (!silent) {
      setLoading(true)
    }

    const params = getApplyInfoParams()

    return Promise.all([
      getExamineeApplyInfo(params),
      getExamApplyTemplate({ examId: props.examineeItem.examId })
    ]).then(([applyInfoRes, templateFieldRes]) => {
      if (requestId !== fetchRequestIdRef.current) {
        return null
      }

      if (!silent) {
        setLoading(false)
      }

      if (applyInfoRes.code !== ResponseCode.success || templateFieldRes.code !== ResponseCode.success) {
        if (!silent) {
          Modal.error({
            title: '错误',
            content: '查询考生报名信息失败'
          })
        }
        return null
      }

      setTemplateField(templateFieldRes.data)
      setApplyInfo(applyInfoRes.data)
      return applyInfoRes.data
    }).catch(err => {
      if (requestId !== fetchRequestIdRef.current) {
        return null
      }

      if (!silent) {
        setLoading(false)
      }
      console.error(err)
      if (!silent) {
        Modal.error({
          title: '错误',
          content: '网络错误，查询考生报名信息失败'
        })
      }
      return null
    })
  }, [getApplyInfoParams, props.examineeItem.examId])

  const scheduleChsiPoll = useCallback((currentVerification) => {
    clearChsiPollTimer()

    if (currentVerification?.chsiVerifyStatus !== 'PENDING') {
      return
    }

    if (chsiPollCountRef.current >= CHSI_POLL_MAX_TIMES) {
      notification.info({
        message: '学信网核验仍在进行中，请稍后刷新查看'
      })
      return
    }

    chsiPollTimerRef.current = setTimeout(() => {
      chsiPollCountRef.current += 1
      getExamineeApplyInfoAction({ silent: true }).then(data => {
        const nextStatus = data?.chsiReportVerification?.chsiVerifyStatus
        if (nextStatus !== 'PENDING') {
          return
        }
        scheduleChsiPoll(data?.chsiReportVerification)
      })
    }, CHSI_POLL_INTERVAL_MS)
  }, [clearChsiPollTimer, getExamineeApplyInfoAction])

  const handleViewChsiDetail = useCallback((verificationId) => {
    chsiDetailModalRef.current?.showModal({
      applyInfoId: props.examineeItem.applyInfoId || props.examineeItem.id,
      verificationId
    })
  }, [props.examineeItem])

  const examineeInfoTab = useMemo(() => ({
    key: 'examineeInfo',
    label: '考生信息',
    children: (
      <ExamineeInfo
        activeKey={ activeKey }
        applyInfo={ applyInfo }
        templateField={ templateField }
        examineeItem={ props.examineeItem }
        modalVisible={ modalVisible }
        uploadList={ props.uploadList }
        onViewChsiDetail={ handleViewChsiDetail }
      ></ExamineeInfo>
    )
  }), [
    activeKey,
    applyInfo,
    handleViewChsiDetail,
    modalVisible,
    props.examineeItem,
    props.uploadList,
    templateField
  ])

  const tabsMap = useMemo(() => ({
    examineeInfo: examineeInfoTab,
    applyFormTable: {
      key: 'applyFormTable',
      label: '报名表',
      forceRender: false,
      children: (
        <ExamineeApplyFormTable
          activeKey={ activeKey }
          applyInfo={ applyInfo }
          examineeItem={ props.examineeItem }
          modalVisible={ modalVisible }
        ></ExamineeApplyFormTable>
      )
    },
    examCertificate: {
      key: 'examCertificate',
      label: '笔试准考证',
      forceRender: false,
      children: (<ExamineeAdmissionTicket
        activeKey={ activeKey }
        applyInfo={ applyInfo }
        examineeItem={ props.examineeItem }
        modalVisible={ modalVisible }
      ></ExamineeAdmissionTicket>)
    },
    interviewNotice: {
      key: 'interviewNotice',
      label: '面试准考证',
      forceRender: false,
      children: (<ExamineeInterviewAdmission
        activeKey={ activeKey }
        examineeItem={ props.examineeItem }
        modalVisible={ modalVisible }
      ></ExamineeInterviewAdmission>)
    },
    publicResult: {
      key: 'publicResult',
      label: '成绩查询',
      forceRender: false,
      children: (
        <ExamineePublicResult
          activeKey={ activeKey }
          applyInfo={ applyInfo }
          templateField={ templateField }
          examineeItem={ props.examineeItem }
          modalVisible={ modalVisible }
        ></ExamineePublicResult>
      )
    },
    paymentInformation: {
      key: 'paymentInformation',
      label: '缴费信息',
      forceRender: false,
      children: (
        <PaymentInformation
          activeKey={ activeKey }
          applyInfo={ applyInfo }
          templateField={ templateField }
          examineeItem={ props.examineeItem }
          modalVisible={ modalVisible }
        ></PaymentInformation>
      )
    }
  }), [activeKey, applyInfo, examineeInfoTab, modalVisible, props.examineeItem, templateField])

  useEffect(() => {
    if (applyInfo && templateField) {
      const nextTabsItems = props.activeTabs?.map(key => {
        return tabsMap[key]
      }).filter(item => {
        if (item.key === 'paymentInformation') {
          return applyInfo.payStatus === 1
        } else {
          return true
        }
      }) || []

      setExamineeTabsItems(nextTabsItems)

      if (modalVisible && nextTabsItems.length > 0) {
        const defaultKey = nextTabsItems.find(item => item.key === 'examineeInfo')?.key || nextTabsItems[0].key
        setActiveKey(prev => nextTabsItems.some(item => item.key === prev) ? prev : defaultKey)
      }
    }
  }, [applyInfo, modalVisible, props.activeTabs, tabsMap, templateField])

  useEffect(() => {
    if (modalVisible && props.examineeItem.examId) {
      getExamineeApplyInfoAction()
      if (props.modalType === 'view') {
        setModalTitle('考生信息')
      } else {
        setModalTitle('考生报名信息详情')
      }
    }

    if (modalVisible && (props.examineeItem?.id || props.examineeItem?.applyInfoId)) {
      eventBus.emit('reloadOperatingRecord', props.examineeItem.id || props.examineeItem.applyInfoId)
    }
  }, [getExamineeApplyInfoAction, modalVisible, props.examineeItem, props.modalType])

  useEffect(() => {
    const reloadApplyInfoListener = (applyInfoId) => {
      const currentId = props.examineeItem.applyInfoId || props.examineeItem.id
      if (modalVisible && applyInfoId === currentId) {
        getExamineeApplyInfoAction({ silent: true })
      }
    }

    eventBus.addListener('reloadExamineeApplyInfo', reloadApplyInfoListener)

    return () => {
      eventBus.removeListener('reloadExamineeApplyInfo', reloadApplyInfoListener)
    }
  }, [getExamineeApplyInfoAction, modalVisible, props.examineeItem])

  useEffect(() => {
    if (!modalVisible) {
      return
    }

    const chsiStatus = applyInfo?.chsiReportVerification?.chsiVerifyStatus

    if (chsiStatus === 'PENDING' && !chsiPollTimerRef.current && chsiPollCountRef.current === 0) {
      scheduleChsiPoll(applyInfo.chsiReportVerification)
    }

    if (chsiStatus && chsiStatus !== 'PENDING') {
      resetChsiPoll()
    }

    return () => {
      clearChsiPollTimer()
    }
  }, [
    applyInfo?.chsiReportVerification,
    clearChsiPollTimer,
    modalVisible,
    resetChsiPoll,
    scheduleChsiPoll
  ])

  const showModal = () => {
    setActiveKey('examineeInfo')
    setModalVisible(true)
  }

  const hideModal = () => {
    resetExamineeData()
    setModalVisible(false)
  }

  const checkPass = () => {
    Modal.confirm({
      title: '信息确认',
      content: '确认该考生通过审核吗？',
      onOk: () => {
        const params = {
          applyInfoId: props.examineeItem.applyInfoId || props.examineeItem.id,
          auditStatus: 2,
          optLock: applyInfo.optLock,
          userName: userInfo.realName
        }

        setLoading(true)
        updateExamineeCheckStatus(params, userInfo.currentRoleTag).then(res => {
          setLoading(false)
          if (res.code === ResponseCode.success) {
            notification.success({
              message: '操作成功'
            })
            needUploadList()
          } else {
            Modal.error({
              title: '错误',
              content: res.msg
            })
          }
        }).catch(err => {
          console.error(err)
          setLoading(false)
          Modal.error({
            title: '错误',
            content: '网络错误，修改审核状态失败'
          })
        })
      }
    })
  }

  const showNotPassModal = () => {
    refuseCheckModalRef.current.showModal()
  }

  const showNotPassAndForbidModal = () => {
    refuseCheckModalRef.current.showModal({ forbidReapplyPosition: true })
  }

  const examineeCheckControlBarVisible = (info) => {
    const currentTime = selectedExam.timeStamp || new Date().getTime()
    const reviewStage = info?.reviewStage ?? props.examineeItem?.reviewStage
    const roleCanCheck = canRoleCheckExamineeByReviewStage(
      userInfo.currentRoleTag,
      reviewStage,
      selectedExam
    )
    const withinCheckPeriod = userInfo.currentRoleTag === 'superAdmin'
      || (selectedExam.checkEndTime > currentTime)

    if (props.pageType === 'check' && roleCanCheck && withinCheckPeriod) {
      return (
        <div className={ 'w-100 mt-3 d-flex align-items-center justify-content-center' }>
          <Space size={ 24 } wrap>
            {
              info?.saveStatus !== 2
                ? <Button
                  type={ 'primary' }
                  onClick={ checkPass }
                >审核通过</Button>
                : <></>
            }
            {
              info?.saveStatus !== -1 && info?.saveStatus !== 2
                ? <Button
                  type={ 'primary' }
                  danger
                  onClick={ showNotPassModal }
                >审核不通过</Button>
                : <></>
            }
            {
              info?.saveStatus !== -1 && info?.saveStatus !== 2 && selectedExam.forbidReapplyUnitStatus === 1
                ? <Button
                  danger
                  onClick={ showNotPassAndForbidModal }
                >审核不通过，且禁止报考本岗位</Button>
                : <></>
            }
          </Space>
        </div>
      )
    } else {
      return <></>
    }
  }

  const activeKeyChange = (key) => {
    setActiveKey(key)
  }

  const needUploadList = () => {
    hideModal()
    props.uploadList?.()
  }

  return (
    <div ref={ ref }>
      <DraggableModal
        width={ 1180 }
        title={ modalTitle }
        open={ modalVisible }
        footer={ false }
        onCancel={ hideModal }
      >
        <Spin spinning={ loading }>
          <Tabs destroyInactiveTabPane activeKey={ activeKey } items={ examineeTabsItems } onChange={ activeKeyChange }></Tabs>

          { examineeCheckControlBarVisible(applyInfo) }
        </Spin>
      </DraggableModal>

      <RefuseCheckModal
        ref={ refuseCheckModalRef }
        examineeItem={ props.examineeItem }
        applyInfo={ applyInfo }
        orgId={ selectedExam?.orgId }
        insertSuccess={ needUploadList }
      ></RefuseCheckModal>

      <ChsiReportVerificationDetailModal ref={ chsiDetailModalRef }></ChsiReportVerificationDetailModal>
    </div>
  )
})

export default ExamineeInfoModal
